import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, MessageSquare, Sparkles, Code, ChevronDown, Edit2, Check, PlusCircle, Trash2, Loader2, Copy, X, Edit3, MessageCircle, Stethoscope, ClipboardList, AlertTriangle, Heart, Calendar, ThumbsUp, ThumbsDown } from "lucide-react";
import { message as antMessage } from "antd";
import { ProcessedContentViewProps, ProcessedContent } from "./interfaces";
import axiosInstance from "../../config/axios";
import Confetti from 'react-confetti';
import { useTextSelection } from "../../hooks/useTextSelection";
import { EvidenceSearchButton } from "./EvidenceSearchButton";
import { EvidenceResultModal } from "./EvidenceResultModal";
import { evidenceService, EvidenceSearchResponse } from "../../services/evidenceService";

interface EditableProcessedContentViewProps {
  content: ProcessedContent;
  onContentChange?: (updatedContent: ProcessedContent) => void;
  readOnly?: boolean;
  activeTab?: string;
  saveToApi?: (content: ProcessedContent) => Promise<void>;
  recordingId?: string;
  fullNotes?: any; // Full notes object including transcript and other data
}

export const ProcessedContentView: React.FC<EditableProcessedContentViewProps> = ({
  content,
  // Note: We're completely ignoring the readOnly prop
  readOnly: _readOnly = false,
  onContentChange,
  recordingId,
  activeTab = "soap",
  fullNotes,
}) => {
  // Always allow editing regardless of readOnly prop
  const forceEditMode = true; // This ensures edit buttons are always visible
  
  // Initialize all sections as not collapsed
  const [collapsedSections, setCollapsedSections] = useState<{
    [key: string]: boolean;
  }>({});

  // Add state for feedback
  const [likedSections, setLikedSections] = useState<{ [key: string]: boolean }>({});
  const [dislikedSections, setDislikedSections] = useState<{ [key: string]: boolean }>({});
  const [confettiSections, setConfettiSections] = useState<{ [key: string]: boolean }>({});
  const [feedbackCounts, setFeedbackCounts] = useState<{ [key: string]: { likes: number, dislikes: number } }>({});

  // Add evidence search functionality
  const containerRef = useRef<HTMLDivElement>(null);
  const { selectedText, showButton, buttonPosition, clearSelection } = useTextSelection(containerRef);
  const [evidenceResult, setEvidenceResult] = useState<EvidenceSearchResponse | null>(null);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [storedSelectedText, setStoredSelectedText] = useState<string>(''); // Store selected text during API call
  const [isSearching, setIsSearching] = useState(false); // Track if evidence search is in progress

  // Effect to initialize collapsed sections when content changes
  useEffect(() => {
    if (content) {
      const initialCollapsedState = Object.keys(content).reduce((acc, key) => {
        acc[key] = false; // Set all sections to not collapsed initially
        return acc;
      }, {} as { [key: string]: boolean });
      
      setCollapsedSections(initialCollapsedState);
    }
  }, []);  // Run only on mount
  
  // Add state for tracking which section is being edited
  const [editingSection, setEditingSection] = useState<string | null>(null);
  
  // State for edited content
  const [editedContent, setEditedContent] = useState<any>(content || {});
  
  // Update editedContent when content prop changes
  useEffect(() => {
    setEditedContent(content || {});
  }, [content]);

  // Add loading and error states
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Evidence search handler
  const handleEvidenceSearch = async (selectedText: string) => {
    try {
      if (!fullNotes?.transcript) {
        antMessage.error('No transcript available for evidence search');
        return;
      }

      // Set searching state and store selected text
      setIsSearching(true);
      setStoredSelectedText(selectedText);

      const result = await evidenceService.searchEvidence(
        selectedText,
        fullNotes.transcript,
        'openai'
      );

      setEvidenceResult(result);
      setShowEvidenceModal(true);
      
      // Clear selection and searching state after modal is shown
      setTimeout(() => {
        setIsSearching(false);
        clearSelection();
      }, 200);

      antMessage.success('Evidence analysis completed!');
    } catch (error) {
      console.error('Evidence search failed:', error);
      antMessage.error('Failed to search for evidence. Please try again.');
      // Clear states on error
      setIsSearching(false);
      setStoredSelectedText('');
    }
  };
  
  // Effect to initialize feedback counts
  useEffect(() => {
    if (content) {
      const initialFeedbackCounts = Object.keys(content).reduce((acc, key) => {
        // Start with random numbers between 5-25 for likes and 0-5 for dislikes
        // This is just for demonstration - in a real app you'd get this from an API
        acc[key] = {
          likes: Math.floor(Math.random() * 20) + 5,
          dislikes: Math.floor(Math.random() * 5)
        };
        return acc;
      }, {} as { [key: string]: { likes: number, dislikes: number } });
      
      setFeedbackCounts(initialFeedbackCounts);
    }
  }, [content]);

  // Function to toggle section collapse/expand
  const toggleSection = (key: string) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Toggle edit mode for a section
  const toggleEditSection = (section: string) => {
    if (editingSection === section) {
      // When exiting edit mode, just close it without saving
      setEditingSection(null);
    } else {
      setEditingSection(section);
    }
  };

  // Helper function to format keys for display
  const formatKey = (key: string): string => {
    // Handle special cases first
    if (key === 'icd_codes') return 'ICD Codes';
    if (key === 'cpt_codes') return 'CPT Codes';
    if (key === 'subjective') return 'Subjective';
    if (key === 'objective') return 'Objective';
    if (key === 'assessment') return 'Assessment';
    if (key === 'plan') return 'Plan';
    if (key === 'follow_up') return 'Follow-up';
    if (key === 'red_flags') return 'Red Flags';
    
    // For other keys, capitalize and replace underscores with spaces
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Helper function to format value for display
  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return "";
    if (typeof value === "object") {
      return JSON.stringify(value);
    }
    const str = String(value);
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Helper function to get icon and color based on section name
  const getSectionIcon = (key: string) => {
    switch (key.toLowerCase()) {
      case "subjective":
        return <MessageCircle className="w-5 h-5 text-blue-600" />;
      case "objective":
        return <Stethoscope className="w-5 h-5 text-green-600" />;
      case "assessment":
        return <FileText className="w-5 h-5 text-amber-600" />;
      case "plan":
        return <ClipboardList className="w-5 h-5 text-purple-600" />;
      case "red_flags":
        return <AlertTriangle className="w-5 h-5 text-red-600" />;
      case "lifestyle_recommendations":
        return <Heart className="w-5 h-5 text-pink-600" />;
      case "follow_up":
        return <Calendar className="w-5 h-5 text-indigo-600" />;
      default:
        return <FileText className="w-5 h-5 text-gray-600" />;
    }
  };

  // Add handler functions for editing content
  const handleValueChange = (key: string, value: string) => {
    const updatedContent = { ...editedContent };
    updatedContent[key] = value;
    setEditedContent(updatedContent);
  };

  const handleNestedValueChange = (parentKey: string, childKey: string, value: string) => {
    const updatedContent = { ...editedContent };
    if (typeof updatedContent[parentKey] === 'object' && updatedContent[parentKey] !== null) {
      updatedContent[parentKey] = {
        ...updatedContent[parentKey],
        [childKey]: value
      };
    }
    setEditedContent(updatedContent);
  };

  const handleNestedArrayChange = (parentKey: string, childKey: string, value: string) => {
    const updatedContent = { ...editedContent };
    if (typeof updatedContent[parentKey] === 'object' && updatedContent[parentKey] !== null) {
      // Split by newlines and trim each line
      const arrayValues = value.split('\n').map(item => item.trim()).filter(item => item.length > 0);
      updatedContent[parentKey] = {
        ...updatedContent[parentKey],
        [childKey]: arrayValues
      };
    }
    setEditedContent(updatedContent);
  };

  // Create a function to save changes for a specific section
  const saveSection = async (sectionKey: string) => {
    if (!recordingId) {
      console.error("No recording ID provided for saving");
      setSaveError("Cannot save: Missing recording ID");
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      // Notify the parent component of the change
      if (onContentChange) {
        onContentChange(editedContent);
      }
      
      // Make API request
      const response = await axiosInstance.put(
        `/api/scribing/speech/recordings/${recordingId}/transcript`, 
        {
          data: {
            recordingId: String(recordingId),
            scribed: editedContent
          }
        }
      );

      if (response.status !== 200) {
        throw new Error(`API responded with status: ${response.status}`);
      }

      // Exit edit mode for this section
      setEditingSection(null);

      // Show success notification
      antMessage.success("Changes saved successfully!");
    } catch (error) {
      console.error("Error saving changes:", error);
      setSaveError("Failed to save changes. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  // Add feedback functions
  const handleLike = (sectionKey: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Toggle like status
    const newLikeStatus = !likedSections[sectionKey];
    setLikedSections(prev => ({
      ...prev,
      [sectionKey]: newLikeStatus
    }));
    
    // Update counts (still keep the counts in state even if not displayed)
    setFeedbackCounts(prev => {
      const current = prev[sectionKey] || { likes: 0, dislikes: 0 };
      return {
        ...prev,
        [sectionKey]: {
          ...current,
          likes: current.likes + (newLikeStatus ? 1 : -1)
        }
      };
    });
    
    // If we're liking the section
    if (newLikeStatus) {
      // Remove dislike if exists
      if (dislikedSections[sectionKey]) {
        setDislikedSections(prev => ({
          ...prev,
          [sectionKey]: false
        }));
        
        // Also adjust dislike count
        setFeedbackCounts(prev => {
          const current = prev[sectionKey] || { likes: 0, dislikes: 0 };
          return {
            ...prev,
            [sectionKey]: {
              ...current,
              dislikes: Math.max(0, current.dislikes - 1)
            }
          };
        });
      }
      
      // Show confetti for this section
      setConfettiSections(prev => ({
        ...prev,
        [sectionKey]: true
      }));
      
      // Let confetti animation run for 6 seconds
      setTimeout(() => {
        setConfettiSections(prev => ({
          ...prev,
          [sectionKey]: false
        }));
      }, 6000);
      
      // Show success message
      antMessage.success({
        content: "Thanks for your feedback! We'll use it to improve our AI.",
        duration: 2,
        style: {
          marginTop: '20px',
        }
      });
    }
  };
  
  const handleDislike = (sectionKey: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Toggle dislike status
    const newDislikeStatus = !dislikedSections[sectionKey];
    setDislikedSections(prev => ({
      ...prev,
      [sectionKey]: newDislikeStatus
    }));
    
    // Update counts
    setFeedbackCounts(prev => {
      const current = prev[sectionKey] || { likes: 0, dislikes: 0 };
      return {
        ...prev,
        [sectionKey]: {
          ...current,
          dislikes: current.dislikes + (newDislikeStatus ? 1 : -1)
        }
      };
    });
    
    // If we're disliking the section
    if (newDislikeStatus) {
      // Remove like if exists
      if (likedSections[sectionKey]) {
        setLikedSections(prev => ({
          ...prev,
          [sectionKey]: false
        }));
        
        // Also adjust like count
        setFeedbackCounts(prev => {
          const current = prev[sectionKey] || { likes: 0, dislikes: 0 };
          return {
            ...prev,
            [sectionKey]: {
              ...current,
              likes: Math.max(0, current.likes - 1)
            }
          };
        });
      }
      
      // Show message
      antMessage.info({
        content: "We appreciate your feedback and will make this better!",
        duration: 3,
        style: {
          marginTop: '20px',
        }
      });
    }
  };

  // Update the renderContent function to include collapsible sections
  const renderContent = (content: any) => {
    switch (activeTab) {
      case "soap":
        return (
          <div className="space-y-6">
            {Object.entries(content).map(([key, value]) => {
              const sectionKey = key;
              const isEditing = editingSection === sectionKey;
              const isCollapsed = collapsedSections[sectionKey];
              const sectionIcon = getSectionIcon(sectionKey);
              const isLiked = likedSections[sectionKey];
              const isDisliked = dislikedSections[sectionKey];
              const showConfetti = confettiSections[sectionKey];
              
              return (
                <div 
                  key={sectionKey} 
                  className={`p-4 rounded-xl transition-all relative user-select-text ${
                    isEditing 
                      ? "bg-blue-50 border border-blue-200 shadow-md" 
                      : "bg-white hover:bg-gray-50/80 border border-gray-100"
                  }`}
                >
                  {showConfetti && (
                    <div className="fixed top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-50">
                      <Confetti
                        width={window.innerWidth}
                        height={window.innerHeight}
                        recycle={false}
                        numberOfPieces={200}
                        gravity={0.200}
                        initialVelocityX={4}
                        initialVelocityY={5}
                        opacity={1}
                        run={true}
                        wind={0}
                        colors={['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4']}
                      />
                    </div>
                  )}
                  
                  <div 
                    className="flex items-center justify-between mb-2 relative z-20"
                  >
                    <div 
                      className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 px-2 py-1 rounded-md transition-colors"
                      onClick={(e) => {
                        // Only toggle if we're not currently selecting text
                        if (!isEditing && !window.getSelection()?.toString()) {
                          toggleSection(sectionKey);
                        }
                      }}
                    >
                      {sectionIcon}
                      <h3 className="text-lg font-medium text-gray-800 tracking-tight">
                        {formatKey(sectionKey)}
                      </h3>
                      <ChevronDown 
                        className={`w-4 h-4 text-gray-500 transition-transform ${isCollapsed ? 'rotate-180' : ''}`} 
                      />
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {/* Like Button */}
                      <button
                        onClick={(e) => handleLike(sectionKey, e)}
                        className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-lg transition-colors ${
                          isLiked 
                            ? "bg-green-100 text-green-700 hover:bg-green-200"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                        title="Like this section"
                      >
                        <ThumbsUp className={`w-3.5 h-3.5 ${isLiked ? 'fill-green-500' : ''}`} />
                      </button>
                      
                      {/* Dislike Button */}
                      <button
                        onClick={(e) => handleDislike(sectionKey, e)}
                        className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-lg transition-colors ${
                          isDisliked 
                            ? "bg-red-100 text-red-700 hover:bg-red-200"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                        title="Dislike this section"
                      >
                        <ThumbsDown className={`w-3.5 h-3.5 ${isDisliked ? 'fill-red-500' : ''}`} />
                      </button>
                      
                      {/* Add copy button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          
                          try {
                            // Format the content based on type
                            let contentToCopy = "";
                            if (typeof value === "object" && value !== null) {
                              contentToCopy = JSON.stringify(value, null, 2);
                            } else if (Array.isArray(value)) {
                              contentToCopy = value.join("\n");
                            } else {
                              contentToCopy = String(value || "");
                            }
                            
                            // Copy to clipboard
                            navigator.clipboard.writeText(contentToCopy);
                            antMessage.success({
                              content: "Copied to clipboard!",
                              duration: 2,
                              style: {
                                marginTop: '20px',
                              }
                            });
                          } catch (error) {
                            console.error("Failed to copy content", error);
                            antMessage.error({
                              content: "Failed to copy content",
                              duration: 2,
                              style: {
                                marginTop: '20px',
                              }
                            });
                          }
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                      
                      {/* Always show the Edit button when forceEditMode is true */}
                      {forceEditMode && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleEditSection(sectionKey);
                            
                            // Also ensure the section is expanded when editing
                            if (isCollapsed) {
                              toggleSection(sectionKey);
                            }
                          }}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                            isEditing
                              ? "bg-blue-600 text-white hover:bg-blue-700"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          }`}
                        >
                          {isEditing ? (
                            <>
                              <X className="w-3.5 h-3.5" />
                              Cancel
                            </>
                          ) : (
                            <>
                              <Edit3 className="w-3.5 h-3.5" />
                              Edit
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <AnimatePresence initial={false}>
                    {!isCollapsed && (
                      <motion.div
                        key={`section-${sectionKey}`}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden"
                      >
                        {isEditing ? (
                          <div className="mt-3 pt-2 border-t border-gray-100">
                            {typeof value === "object" && value !== null ? (
                              <div className="space-y-3">
                                {Object.entries(value as object).map(([subKey, subValue]) => (
                                  <div key={subKey} className="space-y-1">
                                    <label className="block text-sm font-medium text-gray-700">
                                      {formatKey(subKey)}
                                    </label>
                                    {Array.isArray(subValue) ? (
                                      <textarea
                                        className="w-full min-h-[120px] px-3 py-2 text-gray-700 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        value={Array.isArray(subValue) ? subValue.join("\n") : String(subValue || "")}
                                        onChange={(e) => 
                                          handleNestedArrayChange(sectionKey, subKey, e.target.value)
                                        }
                                      />
                                    ) : (
                                      <textarea
                                        className="w-full min-h-[120px] px-3 py-2 text-gray-700 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        value={String(subValue || "")}
                                        onChange={(e) => 
                                          handleNestedValueChange(sectionKey, subKey, e.target.value)
                                        }
                                      />
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <textarea
                                className="w-full min-h-[120px] px-3 py-2 text-gray-700 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                value={String(value || "")}
                                onChange={(e) => handleValueChange(sectionKey, e.target.value)}
                              />
                            )}
                            
                            {/* Add save button for this section */}
                            <div className="flex justify-end mt-3 pt-2 border-t border-gray-100">
                              <div className="flex gap-2">
                                {saveError && (
                                  <div className="p-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium mr-auto">
                                    {saveError}
                                  </div>
                                )}
                                <button
                                  onClick={() => toggleEditSection(sectionKey)}
                                  className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium flex items-center gap-1.5 hover:bg-gray-300 transition-colors"
                                >
                                  <X className="w-3.5 h-3.5" />
                                  Cancel
                                </button>
                                <button
                                  onClick={() => saveSection(sectionKey)}
                                  disabled={isSaving}
                                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium flex items-center gap-1.5 hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {isSaving ? (
                                    <>
                                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                      Saving...
                                    </>
                                  ) : (
                                    <>
                                      <Check className="w-3.5 h-3.5" />
                                      Save
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-1 text-gray-700 leading-relaxed select-text cursor-text">
                            {renderValue(value)}
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        );
      
      case "email":
      case "codes":
      case "transcript":
      case "conversation":
      default:
        return (
          <div className="p-6 text-gray-800 font-medium text-center">
            Please use the content controls from the parent component.
          </div>
        );
    }
  };

  // Helper to render strings containing <br> tags as actual line breaks
  const renderTextWithBreaks = (text: string) => {
    const parts = text.split(/<br\s*\/?>/gi);
    if (parts.length === 1) return text;
    return parts.map((part, i) => (
      <React.Fragment key={i}>
        {part}
        {i < parts.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  // Helper function to render values based on their type
  const renderValue = (value: any) => {
    if (value === null || value === undefined) {
      return <span className="text-gray-400 italic">Not specified</span>;
    }
    
    if (typeof value === "object" && !Array.isArray(value)) {
          return (
      <div className="space-y-4 pl-1 select-text">
        {Object.entries(value).map(([subKey, subValue]) => (
          <div key={subKey} className="space-y-1.5 select-text">
            <div className="text-sm font-medium text-gray-700">{formatKey(subKey)}</div>
            <div className="pl-3 text-gray-700 select-text cursor-text">
                {Array.isArray(subValue) ? (
                  <ul className="list-disc space-y-1.5">
                    {subValue.length === 0 ? (
                      <li className="text-gray-400 italic">None</li>
                    ) : (
                      (subValue as any[]).map((item, i) => {
                        // Check if item is an object (like in conversation or code items)
                        if (typeof item === 'object' && item !== null) {
                          // Handle special cases like code objects
                          if (('code' in item || 'cpt_code' in item || 'icd_code' in item) && 'description' in item) {
                            const codeValue = item.code || item.cpt_code || item.icd_code;
                            return (
                              <li key={i} className="text-gray-700 ml-2 leading-relaxed">
                                <strong>{codeValue}</strong> - {item.description}
                              </li>
                            );
                          }
                          // Handle conversation objects
                          if ('speaker' in item || 'dialogue' in item) {
                            return (
                              <li key={i} className="text-gray-700 ml-2 leading-relaxed">
                                <strong>{item.speaker || 'Unknown'}</strong>: {item.dialogue || ''}
                              </li>
                            );
                          }
                          // Default object rendering
                          return (
                            <li key={i} className="text-gray-700 ml-2 leading-relaxed">
                              {JSON.stringify(item)}
                            </li>
                          );
                        }
                        // Regular string/number items
                        return <li key={i} className="text-gray-700 ml-2 leading-relaxed">{renderTextWithBreaks(String(item))}</li>;
                      })
                    )}
                  </ul>
                ) : (
                  // Check if subValue is a complex object that needs special handling
                  typeof subValue === 'object' && subValue !== null ? (
                    <div className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {JSON.stringify(subValue, null, 2)}
                    </div>
                  ) : (
                    <p className="text-gray-700 whitespace-pre-wrap leading-relaxed select-text cursor-text">{renderTextWithBreaks(String(subValue))}</p>
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      );
    }
    
    if (Array.isArray(value)) {
      return (
        <ul className="list-disc space-y-1.5 pl-2 select-text">
          {value.length === 0 ? (
            <li className="text-gray-400 italic">None</li>
          ) : (
            value.map((item, i) => {
              // Check if item is an object (like in conversation or code items)
              if (typeof item === 'object' && item !== null) {
                // Handle special cases like code objects
                if (('code' in item || 'cpt_code' in item || 'icd_code' in item) && 'description' in item) {
                  const codeValue = item.code || item.cpt_code || item.icd_code;
                  return (
                    <li key={i} className="text-gray-700 ml-2 leading-relaxed">
                      <strong>{codeValue}</strong> - {item.description}
                    </li>
                  );
                }
                // Handle conversation objects
                if ('speaker' in item || 'dialogue' in item) {
                  return (
                    <li key={i} className="text-gray-700 ml-2 leading-relaxed">
                      <strong>{item.speaker || 'Unknown'}</strong>: {item.dialogue || ''}
                    </li>
                  );
                }
                // Default object rendering
                return (
                  <li key={i} className="text-gray-700 ml-2 leading-relaxed">
                    {JSON.stringify(item)}
                  </li>
                );
              }
              // Regular string/number items
              return <li key={i} className="text-gray-700 ml-2 leading-relaxed">{String(item)}</li>;
            })
          )}
        </ul>
      );
    }
    
    return <p className="whitespace-pre-wrap leading-relaxed text-gray-700 select-text cursor-text">{renderTextWithBreaks(String(value))}</p>;
  };

  if (!editedContent || Object.keys(editedContent).length === 0) {
    return (
      <div className="text-center text-gray-500 py-8 text-base">
        No clinical notes available
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div ref={containerRef} className="bg-white rounded-xl shadow-sm p-4 relative select-text" style={{ pointerEvents: 'auto' }}>
        {renderContent(editedContent)}
        
        {/* Evidence Search Button */}
        <EvidenceSearchButton
          show={(showButton && selectedText.length > 0) || isSearching}
          position={buttonPosition}
          selectedText={selectedText || storedSelectedText}
          onSearch={handleEvidenceSearch}
          isLoading={isSearching}
          onClose={() => {
            setIsSearching(false);
            setStoredSelectedText('');
            clearSelection();
          }}
        />
        
        {/* Evidence Result Modal */}
        <EvidenceResultModal
          show={showEvidenceModal}
          evidenceResult={evidenceResult}
          selectedText={storedSelectedText}
          transcript={fullNotes?.transcript}
          onClose={() => {
            setShowEvidenceModal(false);
            setEvidenceResult(null);
            setStoredSelectedText(''); // Clear stored text when closing
            setIsSearching(false); // Clear searching state when closing
          }}
        />
      </div>
    </div>
  );
}; 